import { useState } from 'react'
import { scrollTo } from 'helpers/animateScroll'
import { useResolutions } from 'store/resolutions'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { useApp } from 'store/steps'
import {
    STEPS,
    CreatorStepObject,
    SelfcareStepObject,
    SELFCARE_STEPS,
    UnlimitedCardStepObject,
    UNLIMITED_CARD_STEPS,
    UNLIMITED_RENEW_STEPS,
    CreatorAccommodationStepObject,
    UnlimitedRenewStepObject,
    VouchersStepObject,
    VOUCHERS_STEPS,
} from 'routes/steps'
import { setScrollId, setScrollIndex } from 'store/general-slice'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { useSpaCart } from 'routes/Upsell/SpaCreator/hooks/useSpaCart'

const SCROLL_DURATION = 500
const SCROLL_DELAY = 100

interface AutoScrollI {
    trigerringSection:
        | 'tickets'
        | 'transport'
        | 'cabanas'
        | 'upsell'
        | 'accommodation'
        | 'order'
        | 'starter'
        | 'edit_order'
        | 'summary'
        | 'selfcare_order'
        | 'card_configuration'
        | 'vouchers_configuration'
        | 'vouchers_summary'
        | 'spa'
    trigerringAction:
        | 'show'
        | 'show_with_offer'
        | 'add'
        | 'add_new'
        | 'delete'
        | 'delete_offer'
        | 'delete_all'
        | 'modify'
        | 'edit'
        | 'cancel'
        | 'cancel_empty'
        | 'cancel_edit'
        | 'cancel_create'
        | 'continue_planning'
        | 'choose_bungalow'
        | 'choose_type'
        | 'choose_person'
        | 'choose_card'
        | 'reject_card'
    index?: number
    targetId?: string
    parameter?: 'person' | 'zone' | 'type'
    isRemote?: boolean
    remoteId?: string
    remoteDelay?: number
    hasInstantScroll?: boolean
}

export const useAutoScroll = () => {
    const dispatch = useAppDispatch()
    const { isTablet, isMobile } = useResolutions()
    const { navigateToStep } = useApp()
    const { isBirthdayPromoForSpa, isBlackPromoForCabanas } = usePromotions()
    const { cartItems } = useSpaCart()
    const { ticketBoxes } = useAppSelector((state) => state.ticket)
    const { cardBoxes } = useAppSelector((state) => state.unlimited)
    const { vouchersBoxes } = useAppSelector((state) => state.vouchers)
    const { scrollId, scrollIndex, appMode } = useAppSelector(
        (state) => state.general
    )

    const [scrollTimeout, setScrollTimeout] = useState<NodeJS.Timeout | null>(
        null
    )

    const findUncompletedIndex = (ignoredIndex = -1) =>
        ticketBoxes.findIndex(
            (ticket, index) => !ticket.ticketTime && index !== ignoredIndex
        )

    // TODO: add adding a check if the photo is loaded
    const findUncompletedCardIndex = () => {
        const indexToGo = cardBoxes.findIndex(
            ({ firstName, lastName, dateOfBirth, email, phone }) =>
                !(firstName || lastName || dateOfBirth || email || phone)
        )

        return indexToGo === -1 ? cardBoxes.length : indexToGo
    }

    const findUncompletedVoucherIndex = () => {
        const indexToGo = vouchersBoxes.findIndex(
            ({ amount, personDetail, cardDetail, voucherSignature }) =>
                !(
                    (amount || (personDetail && cardDetail)) &&
                    voucherSignature?.length
                )
        )

        return indexToGo === -1 ? vouchersBoxes.length : indexToGo
    }

    const calculateScroll = (options: AutoScrollI) => {
        const {
            remoteId,
            trigerringSection,
            trigerringAction,
            index,
            targetId,
            parameter,
            isRemote,
            remoteDelay,
            hasInstantScroll,
        } = options

        let id: string = trigerringSection
        // TODO: get rid of if/else/switch logic
        if (trigerringSection === 'tickets') {
            const uncompletedIndex = findUncompletedIndex(index)

            switch (trigerringAction) {
                case 'show':
                    if (uncompletedIndex === -1) id = 'tickets-continue'
                    else id += `-${uncompletedIndex}`

                    break
                case 'show_with_offer':
                    id = 'special-offer'
                    break
                case 'add':
                    if (uncompletedIndex === -1) id += `-${ticketBoxes.length}`
                    else id += `-${uncompletedIndex}`
                    break
                case 'delete':
                    if (ticketBoxes.length === 1) id = 'tickets-settings'
                    else if (uncompletedIndex === -1) id = 'tickets-continue'
                    else
                        id += `-${
                            index && uncompletedIndex > index
                                ? uncompletedIndex - 1
                                : uncompletedIndex
                        }`

                    break
                case 'delete_offer':
                    id = 'tickets-continue'
                    break
                case 'modify': {
                    id += `-${index}`

                    switch (parameter) {
                        case 'person':
                            id += '-zone'
                            break
                        case 'zone':
                            id += '-type'
                            break
                        case 'type': {
                            const uncomplIndex = findUncompletedIndex(index)

                            id =
                                uncomplIndex === -1
                                    ? 'tickets-continue'
                                    : `tickets-${uncomplIndex}`

                            break
                        }
                        default:
                            break
                    }
                    break
                }
                case 'edit':
                    id += `-${index}`
                    break
                default:
                    break
            }
        } else if (trigerringSection === 'transport') {
            switch (trigerringAction) {
                case 'show':
                case 'delete':
                    break
                case 'add':
                case 'edit':
                case 'cancel_edit':
                    id += `-ticket-${index !== undefined ? index : targetId}`
                    break
                case 'cancel_create':
                    id += '-box'
                    break
                default:
                    break
            }
        } else if (trigerringSection === 'cabanas') {
            switch (trigerringAction) {
                case 'show':
                case 'delete':
                    if (trigerringAction === 'show' && isBlackPromoForCabanas)
                        id = 'cabana-promo-info'
                    break
                case 'add':
                case 'edit':
                case 'cancel_edit':
                    id = `cabana-${index !== undefined ? index : targetId}`
                    break
                case 'cancel_create':
                    id += '-box'
                    break
                default:
                    break
            }
        } else if (trigerringSection === 'accommodation') {
            switch (trigerringAction) {
                case 'continue_planning':
                    id += '-guests-assignment'
                    break
                case 'choose_bungalow':
                    id += '-bungalows-offer-1'
                    break
                default:
                    break
            }
        } else if (trigerringSection === 'card_configuration') {
            const uncompletedIndex = findUncompletedCardIndex()

            switch (trigerringAction) {
                case 'show':
                    id = targetId || ''
                    break
                case 'add':
                    id = `${targetId}${uncompletedIndex}`
                    break

                default:
                    break
            }
        } else if (trigerringSection === 'vouchers_configuration') {
            const uncompletedIndex = findUncompletedVoucherIndex()

            switch (trigerringAction) {
                case 'show':
                case 'choose_type':
                case 'choose_person':
                case 'choose_card':
                case 'reject_card':
                    id = targetId || ''
                    break
                case 'add':
                    id = `voucher-${uncompletedIndex}`
                    break

                default:
                    break
            }
        } else if (trigerringSection === 'spa') {
            switch (trigerringAction) {
                case 'show':
                    // after
                    // - opening editor
                    // align it's top with the top of the viewport
                    // or align it's top with the promotion info during an ongoing promotion
                    id = isBirthdayPromoForSpa ? 'spa-promo-info' : 'spa-editor'
                    break
                case 'add':
                    // after
                    // - cancelling configuration or editing, with items in cart
                    // align viewport with top of configured treatments
                    id = 'spa-configured'
                    break
                case 'cancel':
                    // after
                    // - cancelling configuration or editing, with/without items in cart
                    // align viewport with top of configured treatments or with box of spa attraction
                    id = cartItems.length ? 'spa-configured' : 'spa-box'
                    break
                case 'cancel_empty':
                case 'delete':
                case 'delete_all':
                    // after
                    // - cancelling configuration of the only item
                    // - deleting single treatment
                    // - deleting all spa items
                    // align viewport with box of spa attraction
                    id = 'spa-box'
                    break
                default:
                    break
            }
        }

        // if isRemote - first redirect to different step, then set scrolling
        if (isRemote) {
            let localSteps: (
                | CreatorStepObject
                | CreatorAccommodationStepObject
                | SelfcareStepObject
                | UnlimitedCardStepObject
                | UnlimitedRenewStepObject
                | VouchersStepObject
            )[] = appMode === 'selfcare' ? SELFCARE_STEPS : STEPS

            if (appMode === 'unlimited-card') localSteps = UNLIMITED_CARD_STEPS
            if (appMode === 'unlimited-renew')
                localSteps = UNLIMITED_RENEW_STEPS
            if (appMode === 'vouchers') localSteps = VOUCHERS_STEPS

            const stepObj = localSteps.filter(
                (
                    step:
                        | CreatorStepObject
                        | CreatorAccommodationStepObject
                        | SelfcareStepObject
                        | UnlimitedCardStepObject
                        | UnlimitedRenewStepObject
                        | VouchersStepObject
                ) => step.id === trigerringSection
            )

            navigateToStep({
                stepId: stepObj[0].id,
                withoutScrollTop: !!remoteId,
            })

            if (remoteId) {
                setTimeout(() => {
                    scrollTo({
                        id: remoteId,
                        duration: SCROLL_DURATION,
                        isMobile,
                    })
                }, remoteDelay || 0)
            }
        }

        // if hasInstantScroll run scrolling immidiately
        if (hasInstantScroll) {
            animatePendingScroll(id, true)
            return
        }

        // otherwise stack id of element to scroll to into redux
        // (waiting to restore using animatePendingScroll)
        if (!remoteId) {
            dispatch(setScrollId(id))
            dispatch(setScrollIndex(scrollIndex + 1))
        }
    }

    const animatePendingScroll = (
        targetId: string,
        hasInstantScroll?: boolean
    ) => {
        if (targetId !== scrollId && !hasInstantScroll) return

        scrollTimeout && clearTimeout(scrollTimeout)

        setScrollTimeout(
            setTimeout(() => {
                scrollTo({
                    id: targetId,
                    duration: SCROLL_DURATION,
                    margin: isTablet ? 76 : 24,
                })
                dispatch(setScrollId(''))
            }, SCROLL_DELAY)
        )
    }

    return { calculateScroll, animatePendingScroll, scrollIndex }
}
